import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { LitPage } from '../components/lit-page';
import { PUZZLE_IDS, isPuzzleId, type PuzzleId } from '../components/luna-common';
import { navigate } from '../navigation';
import { getCurrentUser } from '../auth/session';
import { getPuzzles } from '../puzzles/puzzleBase';
import { loadGameStates, type GameStateSummary } from '../puzzles/game-state';

import '../components/luna-game-card-simple/luna-game-card-simple';
import '../components/luna-board-art/luna-board-art';
import '../components/luna-icon/luna-icon';

interface HomePuzzle {
  id: PuzzleId;
  name: string;
}

@customElement('app-home')
export class AppHome extends LitPage {
  @state() private _puzzles: HomePuzzle[] = [];
  @state() private _resume: GameStateSummary | null = null;
  @state() private _resumeName = '';

  async firstUpdated() {
    const puzzles = await getPuzzles();
    this._puzzles = PUZZLE_IDS.map((id) => {
      const model = puzzles.find((puzzle) => puzzle.name.toLowerCase() === id);
      return { id, name: model?.name ?? id };
    });

    const states = await loadGameStates();
    this._resume = states?.[0] ?? null;
    if (this._resume) {
      const model = puzzles.find((puzzle) => puzzle.name.toLowerCase() === this._resume!.puzzle);
      this._resumeName = model?.name ?? this._resume!.puzzle;
    }

    if (window.location.hash === '#puzzles') {
      requestAnimationFrame(() => this.querySelector('#puzzles')?.scrollIntoView({ block: 'start' }));
    }
  }

  private onPlay(puzzle: PuzzleId) {
    navigate(`/puzzles/${puzzle}/boards`);
  }

  private onResumeContinue() {
    if (!this._resume) {
      return;
    }
    if (this._resume.level !== null) {
      navigate(`/puzzles/${this._resume.puzzle}/${this._resume.level}?resume=1`);
    } else {
      navigate(`/puzzles/${this._resume.puzzle}?resume=1`);
    }
  }

  private get _resumePuzzle(): PuzzleId {
    return this._resume && isPuzzleId(this._resume.puzzle) ? this._resume.puzzle : 'map';
  }

  content() {
    const firstName = getCurrentUser()?.name?.first_name?.trim();
    const greeting = firstName
      ? `Good to see you, ${firstName}. Ready for a puzzle?`
      : 'Good to see you. Ready for a puzzle?';

    return html`
      <main class="app-home">
        <section class="app-home__hero">
          <h1 class="mbw-type-hero-h1">${greeting}</h1>
          <p class="mbw-type-body-large">
            Take your time. Pick anything below — you can stop and come back whenever you like.
          </p>
        </section>

        ${this._resume
          ? html`
              <section
                class="app-home__resume"
                role="region"
                aria-label="Pick up where you left off"
              >
                <span
                  class="app-home__resume-thumb app-home__resume-thumb--${this._resumePuzzle}"
                >
                  <luna-board-art puzzle=${this._resumePuzzle} size="60"></luna-board-art>
                </span>
                <span class="app-home__resume-text">
                  <span class="app-home__resume-eyebrow mbw-type-eyebrow"
                    >Pick up where you left off</span
                  >
                  <span class="app-home__resume-name mbw-type-title-card">${this._resumeName}</span>
                </span>
                <button
                  type="button"
                  class="app-home__resume-continue"
                  @click=${this.onResumeContinue}
                >
                  <span class="app-home__resume-continue-label mbw-type-play-label">Continue</span>
                  <luna-icon name="chevron-right" size="18" aria-hidden="true"></luna-icon>
                </button>
              </section>
            `
          : ''}

        <h2 class="app-home__heading mbw-type-title-section" id="app-home-all-puzzles">
          All puzzles
        </h2>

        <section class="app-home__cards" id="puzzles" aria-labelledby="app-home-all-puzzles">
          ${this._puzzles.map(
            (puzzle) => html`
              <luna-game-card-simple
                puzzle=${puzzle.id}
                name=${puzzle.name}
                @luna-play=${() => this.onPlay(puzzle.id)}
              ></luna-game-card-simple>
            `
          )}
        </section>
      </main>
    `;
  }

  styles() {
    return css`
      .app-home {
        box-sizing: border-box;
        padding: 34px 34px 40px; /* pinned: figma Home desktop */
      }

      .app-home__hero {
        display: flex;
        flex-direction: column;
        gap: 8px; /* pinned: figma Home hero */
        box-sizing: border-box;
        padding: 26px 30px; /* pinned: figma Home desktop */
        border-radius: var(--luna-radius-card);
        background: var(--mbw-surface-card);
        box-shadow:
          inset 0 0 0 1px var(--mbw-card-hairline),
          0 4px 0 var(--mbw-card-ledge),
          0 10px 22px rgba(20, 40, 90, 0.1); /* pinned: figma luna/card-lift-blue */
      }

      .app-home__hero h1 {
        margin: 0;
        color: var(--mbw-text-ink);
      }

      .app-home__hero p {
        margin: 0;
        color: var(--mbw-text-body);
      }

      .app-home__resume {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 20px; /* pinned: figma Home desktop */
        box-sizing: border-box;
        margin-top: 36px; /* pinned: figma Home desktop rhythm */
        padding: 18px 22px; /* pinned: figma Home desktop */
        border-radius: var(--luna-radius-card-sm);
        background-image: linear-gradient(
          180deg,
          #4a9bff 0%,
          #1358d8 14%,
          #0a46c4 60%,
          #0d51d6 100%
        ); /* pinned: figma Home resume band */
        box-shadow:
          inset 0 1px 0 rgba(255, 255, 255, 0.5),
          0 3px 0 var(--luna-edge-blue); /* pinned: figma Home resume band */
      }

      .app-home__resume-thumb {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 60px; /* pinned: figma Home desktop */
        height: 60px; /* pinned: figma Home desktop */
        border-radius: 12px; /* pinned: figma Home resume band */
        overflow: hidden; /* clip the full-bleed board art to the rounded thumb */
      }

      /* Flex item, not inline text-baseline content — no descender gap under the
         svg, so the art fills the thumb exactly (no crop/shift). */
      .app-home__resume-thumb luna-board-art {
        display: flex;
      }

      .app-home__resume-thumb--map {
        background: var(--mbw-palette-map-tint);
      }

      .app-home__resume-thumb--pegs {
        background: var(--mbw-palette-pegs-tint);
      }

      .app-home__resume-thumb--rectangles {
        background: var(--mbw-palette-rectangles-tint);
      }

      .app-home__resume-thumb--tents {
        background: var(--mbw-palette-tents-tint);
      }

      .app-home__resume-thumb--unruly {
        background: var(--mbw-palette-unruly-tint);
      }

      .app-home__resume-thumb--untangle {
        background: var(--mbw-palette-untangle-tint);
      }

      .app-home__resume-text {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
      }

      .app-home__resume-eyebrow {
        color: #d7e6ff; /* pinned: figma Home resume band (not a token) */
        font-size: 13px; /* pinned: figma Home desktop (0.06em = 0.78px tracking) */
      }

      .app-home__resume-name {
        color: #ffffff; /* pinned: figma Home resume band */
      }

      .app-home__resume-continue {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 8px; /* pinned: figma Home resume band */
        width: 220px; /* pinned: figma Home desktop */
        height: 60px; /* pinned: figma Home desktop */
        box-sizing: border-box;
        border: none;
        border-radius: var(--luna-radius-btn);
        cursor: pointer;
        background: #ffffff; /* pinned: figma Home resume band */
        color: var(--luna-edge-blue);
        box-shadow: 0 3px 0 var(--luna-edge-blue); /* pinned: figma Home resume band */
      }

      .app-home__resume-continue:active {
        box-shadow: 0 1px 0 var(--luna-edge-blue);
      }

      .app-home__resume-continue:focus-visible {
        outline: none;
        box-shadow:
          0 3px 0 var(--luna-edge-blue),
          0 0 0 5px #ffffff,
          0 0 0 7px var(--cds-focus);
      }

      .app-home__heading {
        margin: 36px 0 0; /* pinned: figma Home desktop rhythm */
        color: var(--mbw-text-ink);
      }

      .app-home__cards {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 32px; /* pinned: figma Home desktop */
        margin-top: 36px; /* pinned: figma Home desktop rhythm */
      }

      .app-home__cards luna-game-card-simple {
        width: 100%;
        transition: opacity var(--mbw-motion-dur-fast);
      }

      /* Siblings dim so the hovered/focused tile is unmistakable. */
      .app-home__cards:hover luna-game-card-simple:not(:hover),
      .app-home__cards:focus-within luna-game-card-simple:not(:focus-within) {
        opacity: 0.85; /* pinned: figma Game Card — Simple */
      }

      @media (max-width: 1279px) {
        .app-home {
          padding: 26px 26px 34px; /* pinned: figma Home tablet */
        }

        .app-home__hero {
          padding: 22px 24px; /* pinned: figma Home tablet */
        }

        .app-home__resume {
          margin-top: 30px; /* pinned: figma Home tablet rhythm */
        }

        .app-home__resume-thumb {
          width: 52px; /* pinned: figma Home tablet */
          height: 52px; /* pinned: figma Home tablet */
        }

        .app-home__resume-thumb luna-board-art svg {
          width: 52px;
          height: 52px;
        }

        .app-home__resume-eyebrow {
          font-size: 12px; /* pinned: figma Home tablet (0.06em = 0.72px tracking) */
        }

        .app-home__resume-continue {
          width: 200px; /* pinned: figma Home tablet */
          height: 56px; /* pinned: figma Home tablet */
        }

        .app-home__resume-continue-label {
          font-size: 18px; /* pinned: figma Home tablet */
        }

        .app-home__resume-continue luna-icon svg {
          width: 16px; /* pinned: figma Home tablet */
          height: 16px; /* pinned: figma Home tablet */
        }

        .app-home__heading {
          margin-top: 30px; /* pinned: figma Home tablet rhythm */
        }

        .app-home__cards {
          grid-template-columns: repeat(2, 1fr);
          gap: 28px; /* pinned: figma Home tablet */
          margin-top: 30px; /* pinned: figma Home tablet rhythm */
        }
      }

      @media (max-width: 767px) {
        .app-home {
          padding: 18px 16px 24px; /* pinned: figma Home mobile */
        }

        .app-home__hero {
          padding: 18px; /* pinned: figma Home mobile */
        }

        .app-home__resume {
          gap: 14px; /* pinned: figma Home mobile */
          margin-top: 24px; /* pinned: figma Home mobile rhythm */
          padding: 16px 18px; /* pinned: figma Home mobile */
        }

        .app-home__resume-name {
          font-size: 22px; /* pinned: figma Home mobile */
        }

        .app-home__resume-continue {
          width: 100%; /* pinned: figma Home mobile (full-width row) */
        }

        .app-home__heading {
          margin-top: 24px; /* pinned: figma Home mobile rhythm */
        }

        .app-home__cards {
          grid-template-columns: 1fr;
          gap: 20px; /* pinned: figma Home mobile */
          margin-top: 24px; /* pinned: figma Home mobile rhythm */
        }
      }
    `;
  }
}
